// Background uploader (ТЗ §8.5): segments go in order, the server confirms each by number and hash,
// the local copy is deleted only after confirmation; retries with growing pauses; works across reloads.

import { api, ApiError, loadTokens } from "../lib/api.ts";
import { deleteRecording, deleteSegment, putRecording, recordingsOf, segmentsOf, type LocalRecording, type LocalSegment } from "./db.ts";

export interface UploadState {
  online: boolean;
  pendingSegments: number;
  pendingMs: number;
  recordings: { visitId: string; recordingNo: number; patientName?: string; startedAt: string; pending: number; produced: number; finished: boolean }[];
  needsLogin: boolean;
  lastError?: string;
  busy: boolean;
}

type Listener = () => void;

class Uploader {
  state: UploadState = { online: navigator.onLine, pendingSegments: 0, pendingMs: 0, recordings: [], needsLogin: false, busy: false };
  private listeners = new Set<Listener>();
  private running = false;
  private again = false;
  private backoffMs = 1000;
  private timer?: number;
  private synced = new Set<string>();

  constructor() {
    window.addEventListener("online", () => {
      this.set({ online: true });
      this.backoffMs = 1000;
      this.kick();
    });
    window.addEventListener("offline", () => this.set({ online: false }));
    window.addEventListener("aisol:tokens", () => {
      this.synced.clear();
      this.kick();
    });
    document.addEventListener("visibilitychange", () => document.visibilityState === "visible" && this.kick());
    setInterval(() => this.kick(), 15_000);
  }

  subscribe = (l: Listener) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };
  getState = () => this.state;
  private set(p: Partial<UploadState>) {
    this.state = { ...this.state, ...p };
    this.listeners.forEach((l) => l());
  }

  private userId(): string | undefined {
    return loadTokens()?.userId;
  }

  kick() {
    if (this.running) {
      this.again = true;
      return;
    }
    clearTimeout(this.timer);
    void this.run();
  }

  async refreshCounts(): Promise<void> {
    const uid = this.userId();
    if (!uid) return;
    const recs = await recordingsOf(uid);
    let pending = 0;
    let pendingMs = 0;
    const list: UploadState["recordings"] = [];
    for (const r of recs) {
      const segs = await segmentsOf(uid, r.visitId, r.recordingNo);
      pending += segs.length;
      pendingMs += segs.reduce((a, s) => a + s.durationMs, 0);
      list.push({ visitId: r.visitId, recordingNo: r.recordingNo, patientName: r.patientName, startedAt: r.startedAt, pending: segs.length, produced: r.produced, finished: !!r.finished });
    }
    this.set({ pendingSegments: pending, pendingMs, recordings: list });
  }

  private async run(): Promise<void> {
    this.running = true;
    this.set({ busy: true });
    try {
      do {
        this.again = false;
        await this.cycle();
      } while (this.again);
    } finally {
      this.running = false;
      this.set({ busy: false });
      await this.refreshCounts().catch(() => {});
    }
  }

  private async cycle(): Promise<void> {
    const uid = this.userId();
    if (!uid) {
      this.set({ needsLogin: (await this.anyPending()) > 0 });
      return;
    }
    this.set({ needsLogin: false });
    const recs = await recordingsOf(uid);
    for (const rec of recs) {
      const ok = await this.processRecording(uid, rec);
      if (!ok) {
        this.schedule();
        return;
      }
    }
    this.backoffMs = 1000;
    this.set({ lastError: undefined });
  }

  private schedule() {
    clearTimeout(this.timer);
    this.timer = window.setTimeout(() => this.kick(), this.backoffMs);
    this.backoffMs = Math.min(this.backoffMs * 2, 60_000);
  }

  private async anyPending(): Promise<number> {
    const { allRecordings } = await import("./db.ts");
    return (await allRecordings()).length;
  }

  /** Returns false when the network or the session stopped us. */
  private async processRecording(uid: string, rec: LocalRecording): Promise<boolean> {
    const key = `${rec.visitId}#${rec.recordingNo}`;
    try {
      if (!this.synced.has(key)) {
        // compare with the server: drop what it already confirmed, resend the rest
        const state = await api<{ recording: { endedAt?: string } | null; confirmed: { seq: number; sha256: string }[] }>(`/visits/${rec.visitId}/recordings/${rec.recordingNo}`);
        const confirmed = new Map(state.confirmed.map((c) => [c.seq, c.sha256]));
        for (const s of await segmentsOf(uid, rec.visitId, rec.recordingNo)) {
          if (confirmed.get(s.seq) === s.sha256) await deleteSegment(s);
        }
        this.synced.add(key);
      }
      const segs = await segmentsOf(uid, rec.visitId, rec.recordingNo);
      for (const s of segs) await this.sendSegment(s);
      if (rec.finished && !rec.finishSent) {
        const r = await api<{ status: string }>(`/visits/${rec.visitId}/recordings/${rec.recordingNo}/finish`, {
          body: { segmentsTotal: rec.finished.segmentsTotal, durationMs: rec.finished.durationMs, endedBy: rec.finished.endedBy, endedAt: rec.finished.endedAt },
        });
        rec = { ...rec, finishSent: true, serverStatus: r.status };
        await putRecording(rec);
        window.dispatchEvent(new CustomEvent("aisol:recording-finished", { detail: { visitId: rec.visitId, status: r.status } }));
      }
      if (rec.finished && rec.finishSent && (await segmentsOf(uid, rec.visitId, rec.recordingNo)).length === 0) {
        await deleteRecording(rec);
        window.dispatchEvent(new CustomEvent("aisol:recording-uploaded", { detail: { visitId: rec.visitId } }));
      }
      return true;
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.status === 401 || e.code === "SESSION_EXPIRED") {
          this.set({ needsLogin: true, lastError: "Войдите снова, чтобы догрузить запись" });
          return false;
        }
        if (e.status === 404 || e.status === 403) {
          // the visit is gone or belongs to someone else: keep the data, do not loop on it
          this.set({ lastError: e.message });
          this.synced.add(key);
          return true;
        }
        this.set({ lastError: e.code === "OFFLINE" ? "Нет сети — запись сохранена на телефоне" : e.message });
      }
      return false;
    }
  }

  private async sendSegment(s: LocalSegment): Promise<void> {
    try {
      await api(`/visits/${s.visitId}/recordings/${s.recordingNo}/segments/${s.seq}`, {
        method: "PUT",
        raw: s.blob,
        contentType: s.blob.type || "application/octet-stream",
        query: { stream: s.stream, durationMs: s.durationMs, recordedAt: s.recordedAt, sha256: s.sha256 },
      });
      await deleteSegment(s);
    } catch (e) {
      if (e instanceof ApiError && e.code === "SEGMENT_CONFLICT") {
        // the server keeps the first copy; this one is a duplicate after a crash
        await deleteSegment(s);
        return;
      }
      throw e;
    }
  }

  /** Storage full on the phone: send right away if online (ТЗ §8.5). */
  sendDirect(s: LocalSegment) {
    void this.sendSegment(s).catch(() => this.set({ lastError: "Место на телефоне закончилось, а сети нет — часть записи может потеряться" }));
  }

  event(visitId: string, recordingNo: number, type: "pause" | "resume" | "autopause" | "silence" | "low_battery", reason?: string) {
    if (!navigator.onLine || !this.userId()) return;
    void api(`/visits/${visitId}/recordings/${recordingNo}/events`, { body: { type, reason, at: new Date().toISOString() } }).catch(() => {});
  }

  async pendingFor(visitId: string): Promise<{ pending: number; produced: number }> {
    const uid = this.userId();
    if (!uid) return { pending: 0, produced: 0 };
    const recs = (await recordingsOf(uid)).filter((r) => r.visitId === visitId);
    let pending = 0;
    let produced = 0;
    for (const r of recs) {
      pending += (await segmentsOf(uid, r.visitId, r.recordingNo)).length;
      produced += r.produced;
    }
    return { pending, produced };
  }
}

export const uploader = new Uploader();
